import React, { useState, useEffect, useRef } from "react";
import {SubmitHandler, useForm} from 'react-hook-form';
import {yupResolver} from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {useDispatch, useSelector} from 'react-redux';
import {AuthActions, AuthEffects} from '../../store/auth';
import {RootState} from '../../store';
import {CodeData} from "../../util/interfaces";
import {
    Form,
    FormCard,
    FormCardHeader,
    Line,
    LinkContainer,
    SpanLink, StyledA,
    StyledField,
    SubmitButton
} from "styles/styled";
import {TextInput} from "shared-react";
import {defaultMessageObj} from "../../util/constants";
import styled from "styled-components";
import {Spacing} from "styles/constants";
import MessageCard from "../message-card";
import {AnimatePresence, motion} from "framer-motion";
import {AppActions} from "../../store/app";
import MotionDiv from "../helperComponents/motionDiv";

const schema = yup.object().shape({
    code: yup.string().required(),
});

const Copy = styled.div `{
    text-align: center;
    margin-bottom: ${Spacing.Space4};
}`;

const Verify2fa = () => {
    const [useBackupCode, setUseBackupCode] = useState(false);
    const dispatch = useDispatch();
    const errorMessage = useSelector((state: RootState) => state.auth.verify2faError);
    const { register, handleSubmit, formState: {errors}, setFocus } = useForm({resolver: yupResolver(schema)});
    const onSubmit: SubmitHandler<CodeData> = (data: CodeData) => {
        dispatch(AuthEffects.send2faCode(data));
    };

    const errorCopy = useBackupCode ? 'Invalid backup code. Please try again.' : 'Invalid verification code. Please try again.';

    const returnToLogin = () => {
        dispatch(AppActions.clearUserInfo());
        dispatch(AuthActions.setVerifying2FA(false));
    }

    useEffect(() => {
        setFocus('code');
    }, [setFocus]);

    return (
      <>
          <FormCard>
              <FormCardHeader>2-Step Verification</FormCardHeader>
              <Line/>
              <AnimatePresence exitBeforeEnter>
                  {useBackupCode ? <MotionDiv
                    key="backupCode"
                    duration={0.15}
                  >

                        <MessageCard type={defaultMessageObj.type}>
                            {errorMessage ? errorCopy : null}
                        </MessageCard>
                        <Copy id="2faPrompt">Enter your 12 character backup code.</Copy>
                        <Form onSubmit={handleSubmit(onSubmit)}>
                            <StyledField label="Backup Code" errorMessage='Backup code required' error={errors.code}>
                                <TextInput aria-label="Backup Code" type="text" {...register('code')}/>
                            </StyledField>
                            <SubmitButton type="submit">Submit</SubmitButton>
                        </Form>
                    </MotionDiv>
                  :
                    <MotionDiv
                      key="2FACode"
                      duration={0.15}
                    >
                        <MessageCard type={defaultMessageObj.type}>
                            {errorMessage ? errorCopy : null}
                        </MessageCard>
                        <Copy>
                            <p id="2faPrompt">Enter the 6 digit number generated by the</p>
                            <p>authenticator app on your phone.</p>
                        </Copy>
                        <Form onSubmit={handleSubmit(onSubmit)}>
                            <StyledField label="Verification Code" errorMessage='Verification code required' error={errors.code}>
                                <TextInput aria-label="Verification Code" type="text" {...register('code')}/>
                            </StyledField>
                            <SubmitButton type="submit">Submit</SubmitButton>
                        </Form>
                    </MotionDiv>}
              </AnimatePresence>
          </FormCard>
          <LinkContainer>
              {useBackupCode ? <p>Have your device? <SpanLink role="button" aria-label="authenticator code button" onClick={() => {setUseBackupCode(false)}}>Use an authenticator code</SpanLink></p> :
                <p>Don't have your device? <SpanLink role="button" aria-label="backup code button" onClick={() => {setUseBackupCode(true)}}>Use backup code</SpanLink></p>}
              <p>Return to <StyledA as='span' onClick={returnToLogin} aria-label='login link'>Login</StyledA></p>
          </LinkContainer>
      </>
    )
}

export default Verify2fa;
